import { Switch, Route, useLocation } from "wouter";
import { lazy, Suspense, useEffect } from "react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { HelmetProvider } from 'react-helmet-async';

const Home = lazy(() => import("@/pages/Home"));
const HowItWorksPage = lazy(() => import("@/pages/HowItWorksPage"));
const ServicesPage = lazy(() => import("@/pages/ServicesPage"));
const WhyBrandlarkPage = lazy(() => import("@/pages/WhyBrandlarkPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("@/pages/TermsOfServicePage"));
const EcommerceStoresPage = lazy(() => import("@/pages/EcommerceStoresPage"));
const RevenuePlanPage = lazy(() => import("@/pages/RevenuePlanPage"));
const FashionPage = lazy(() => import("@/pages/FashionPage"));
const HealthBeautyPage = lazy(() => import("@/pages/HealthBeautyPage"));
const ElectronicsPage = lazy(() => import("@/pages/ElectronicsPage"));
const HomeGardenPage = lazy(() => import("@/pages/HomeGardenPage"));
const GCEBlogPost = lazy(() => import("@/pages/GCEBlogPost"));
const CapitalAllocationGapBlogPost = lazy(() => import("@/pages/CapitalAllocationGapBlogPost"));
const BuildVsBuyBlogPost = lazy(() => import("@/pages/BuildVsBuyBlogPost"));
const CustomSystemsVsSaasBlogPost = lazy(() => import("@/pages/CustomSystemsVsSaasBlogPost"));
const KeywordResearchToolBlogPost = lazy(() => import("@/pages/KeywordResearchToolBlogPost"));
const AdSpendMonitoringBlogPost = lazy(() => import("@/pages/AdSpendMonitoringBlogPost"));
const BundleLogicBlogPost = lazy(() => import("@/pages/BundleLogicBlogPost"));
const ExpectationsPage = lazy(() => import("@/pages/ExpectationsPage"));
const AdSpendLeakCheckPage = lazy(() => import("@/pages/AdSpendLeakCheckPage"));
const CapitalAllocationAuditPage = lazy(() => import("@/pages/CapitalAllocationAuditPage"));
const GrowthSystemsPartnershipPage = lazy(() => import("@/pages/GrowthSystemsPartnershipPage"));
const DemandOwnershipPage = lazy(() => import("@/pages/DemandOwnershipPage"));
const AcquisitionEfficiencyPage = lazy(() => import("@/pages/AcquisitionEfficiencyPage"));
const YieldExpansionPage = lazy(() => import("@/pages/YieldExpansionPage"));
const GrowthBlueprintFormSubmissionPage = lazy(() => import("@/pages/GrowthBlueprintFormSubmissionPage"));
const GrowthBlueprintLandingPage = lazy(() => import("@/pages/GrowthBlueprintLandingPage"));
const ApparelPaidSearchLandingPage = lazy(() => import("@/pages/ApparelPaidSearchLandingPage"));
const CaseStudiesPage = lazy(() => import("@/pages/CaseStudiesPage"));
const GrowthCapitalAllocationCaseStudy = lazy(() => import("@/pages/GrowthCapitalAllocationCaseStudy"));
const NotFound = lazy(() => import("@/pages/not-found"));

function ScrollToTop() {
  const [location] = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return null;
}

function Router() {
  return (
    <Suspense fallback={null}>
      <ScrollToTop />
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/how-it-works" component={HowItWorksPage} />
        <Route path="/services" component={ServicesPage} />
        <Route path="/expectations" component={ExpectationsPage} />
        <Route path="/ad-spend-leak-check" component={AdSpendLeakCheckPage} />
        <Route path="/why-brandlark" component={WhyBrandlarkPage} />
        <Route path="/pricing" component={PricingPage} />
        <Route path="/about" component={AboutPage} />
        <Route path="/ecommerce-stores" component={EcommerceStoresPage} />
        <Route path="/growth-blueprint-form" component={RevenuePlanPage} />
        <Route path="/faq" component={FAQPage} />
        <Route path="/contact" component={ContactPage} />
        <Route path="/privacy" component={PrivacyPolicyPage} />
        <Route path="/terms" component={TermsOfServicePage} />
        <Route path="/industries/fashion" component={FashionPage} />
        <Route path="/industries/health-beauty" component={HealthBeautyPage} />
        <Route path="/industries/electronics" component={ElectronicsPage} />
        <Route path="/industries/home-garden" component={HomeGardenPage} />
        <Route path="/blog/growth-capital-efficiency" component={GCEBlogPost} />
        <Route path="/blog/capital-allocation-gap" component={CapitalAllocationGapBlogPost} />
        <Route path="/blog/build-vs-buy-ai-growth-systems" component={BuildVsBuyBlogPost} />
        <Route path="/blog/custom-ai-systems-vs-saas" component={CustomSystemsVsSaasBlogPost} />
        <Route path="/blog/keyword-research-tool" component={KeywordResearchToolBlogPost} />
        <Route path="/blog/ad-spend-monitoring" component={AdSpendMonitoringBlogPost} />
        <Route path="/blog/bundle-logic-system" component={BundleLogicBlogPost} />
        <Route path="/growth-blueprint" component={CapitalAllocationAuditPage} />
        <Route path="/growth-systems-partnership" component={GrowthSystemsPartnershipPage} />
        <Route path="/services/organic-marketing" component={DemandOwnershipPage} />
        <Route path="/services/paid-marketing" component={AcquisitionEfficiencyPage} />
        <Route path="/services/cro" component={YieldExpansionPage} />
        <Route path="/growth-blueprint-form-submission" component={GrowthBlueprintFormSubmissionPage} />
        <Route path="/lp/growth-blueprint" component={GrowthBlueprintLandingPage} />
        <Route path="/lp/apparel-paid-search" component={ApparelPaidSearchLandingPage} />
        <Route path="/case-studies" component={CaseStudiesPage} />
        <Route path="/case-studies/growth-capital-allocation-system" component={GrowthCapitalAllocationCaseStudy} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Router />
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
